import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import type { Component } from "solid-js";
import { createStore } from "solid-js/store";
import { useQueryClient } from "@tanstack/solid-query";
import { queryKeys } from "@core/query/keys";
import {
  saveDreameCloudConfig,
  testDreameConnection,
  useDreameConfigQuery,
} from "../service.v2";
import type { DreameCloudConfig, DreameCloudConfigInput, DreameConnectionTestResult } from "@modules/settings/types";

const resolve = <T,>(input: T | (() => T)): T => (typeof input === "function" ? (input as () => T)() : input);

const toInputPayload = (config: DreameCloudConfig): DreameCloudConfigInput => ({
  region: config.region,
  countryCode: config.countryCode,
  languageCode: config.languageCode,
  username: config.username ?? "",
  deviceAlias: config.deviceAlias ?? "",
});

export const DreameIntegrationView: Component = () => {
  const queryClient = useQueryClient();
  const configQuery = useDreameConfigQuery();
  const [form, setForm] = createStore<DreameCloudConfigInput & { password: string }>({
    region: "us",
    countryCode: "US",
    languageCode: "en",
    username: "",
    password: "",
    deviceAlias: "",
  });
  const [hasExistingPassword, setHasExistingPassword] = createSignal(false);
  const [isDirty, setIsDirty] = createSignal(false);
  const [statusMessage, setStatusMessage] = createSignal<string | null>(null);
  const [statusError, setStatusError] = createSignal<string | null>(null);
  const [isSaving, setIsSaving] = createSignal(false);
  const [isTesting, setIsTesting] = createSignal(false);
  const [testResult, setTestResult] = createSignal<DreameConnectionTestResult | null>(null);

  const currentConfig = createMemo(() => resolve(configQuery.data ?? (() => undefined)) ?? null);

  createEffect(() => {
    const config = currentConfig();
    if (!config) {
      return;
    }
    if (isDirty()) {
      return;
    }
    const payload = toInputPayload(config);
    setForm({ ...payload, password: "" });
    setHasExistingPassword(config.hasPassword);
  });

  const handleInput = (field: keyof typeof form, value: string) => {
    setStatusMessage(null);
    setStatusError(null);
    setIsDirty(true);
    setForm(field, value);
  };

  const handleSave = async (event: Event) => {
    event.preventDefault();
    if (isSaving()) {
      return;
    }
    try {
      setIsSaving(true);
      setStatusError(null);
      const payload: DreameCloudConfigInput = {
        region: form.region.trim(),
        countryCode: form.countryCode.trim(),
        languageCode: form.languageCode.trim(),
        username: form.username.trim(),
        deviceAlias: form.deviceAlias?.trim() || "",
        password: form.password.trim() ? form.password.trim() : undefined,
      };
      if (!payload.region || !payload.countryCode || !payload.languageCode || !payload.username) {
        setStatusError("All required fields must be provided before saving.");
        return;
      }
      const config = await saveDreameCloudConfig(payload);
      setStatusMessage("Integration settings saved.");
      setHasExistingPassword(config.hasPassword);
      setForm({ ...toInputPayload(config), password: "" });
      setIsDirty(false);
      await configQuery.refetch();
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : "Failed to save configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (isTesting()) {
      return;
    }
    setStatusMessage(null);
    setStatusError(null);
    try {
      setIsTesting(true);
      const result = await testDreameConnection();
      setTestResult(result);
      setStatusMessage(result.message || "Test completed.");
      // Invalidate robot list query to refresh the Robot fleet page
      await queryClient.invalidateQueries({ queryKey: queryKeys.robots.root() });
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : "Connection test failed.");
    } finally {
      setIsTesting(false);
    }
  };

  const devices = createMemo(() => testResult()?.devices ?? []);

  return (
    <section class="settings-view" aria-label="Dreame Cloud Integration">
      <header class="settings-view-header">
        <h2>Dreame Cloud Integration</h2>
        <p>
          Connect your Dreame robots to HappyTidy for real-time status monitoring and control.
          Passwords are encrypted and stored securely.
        </p>
      </header>

      <div class="settings-section">
        <div class="settings-section-header">
          <h3>Credentials</h3>
          <p>Configure your Dreame account credentials for API access.</p>
        </div>
        <form id="dreame-form" onSubmit={handleSave}>

        <div class="settings-form-container">
          <div class="settings-row triple">
            <label class="settings-field">
              <span>Region</span>
              <select value={form.region} onInput={(event) => handleInput("region", event.currentTarget.value)}>
                <option value="us">United States (us)</option>
                <option value="eu">Europe (eu)</option>
                <option value="cn">China mainland (cn)</option>
                <option value="sg">Singapore (sg)</option>
              </select>
            </label>
            <label class="settings-field">
              <span>Country</span>
              <input
                type="text"
                value={form.countryCode}
                maxLength={3}
                placeholder="US"
                onInput={(event) => handleInput("countryCode", event.currentTarget.value.toUpperCase())}
              />
            </label>
            <label class="settings-field">
              <span>Language</span>
              <input
                type="text"
                value={form.languageCode}
                maxLength={5}
                placeholder="en"
                onInput={(event) => handleInput("languageCode", event.currentTarget.value)}
              />
            </label>
          </div>
          <div class="settings-row">
            <label class="settings-field">
              <span>Device alias (optional)</span>
              <input
                type="text"
                value={form.deviceAlias ?? ""}
                placeholder="Custom display name for your robot"
                onInput={(event) => handleInput("deviceAlias", event.currentTarget.value)}
              />
            </label>
          </div>
          <div class="settings-row double">
            <label class="settings-field">
              <span>Username</span>
              <input
                type="text"
                value={form.username}
                placeholder="Dreame account username"
                onInput={(event) => handleInput("username", event.currentTarget.value)}
              />
            </label>
            <label class="settings-field">
              <span>Password{hasExistingPassword() ? " (optional)" : ""}</span>
              <input
                type="password"
                value={form.password}
                placeholder={hasExistingPassword() ? "Leave blank to keep current" : "Enter password"}
                onInput={(event) => handleInput("password", event.currentTarget.value)}
              />
            </label>
          </div>
        </div>
        </form>

        <div class="settings-actions">
          <button type="submit" form="dreame-form" class="button-primary" disabled={isSaving()}>
            {isSaving() ? "Saving..." : "Save settings"}
          </button>
          <button type="button" class="button-secondary" onClick={handleTest} disabled={isTesting()}>
            {isTesting() ? "Connecting..." : "Connect"}
          </button>
        </div>

        <Show when={statusMessage()}>
          <div class="settings-inline-status settings-success">{statusMessage()}</div>
        </Show>
        <Show when={statusError()}>
          <div class="settings-inline-status settings-error">{statusError()}</div>
        </Show>
      </div>

      <Show when={devices().length > 0}>
        <div class="settings-section">
          <div class="settings-section-header">
            <h3>Discovered Devices</h3>
            <p>
              {testResult()?.testedAt
                ? `Last connection: ${new Date(testResult()!.testedAt).toLocaleString()}`
                : "Connection results"}
            </p>
          </div>
          <div class="settings-table-wrapper">
            <table class="settings-table">
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Status</th>
                  <th>Battery</th>
                  <th>Model</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                <For each={devices()}>
                  {(device) => (
                    <tr>
                      <td>
                        <div class="settings-device-name">
                          <Show when={device.online}>
                            <span class="settings-status-dot online"></span>
                          </Show>
                          <Show when={!device.online}>
                            <span class="settings-status-dot offline"></span>
                          </Show>
                          {device.displayName || device.did}
                        </div>
                      </td>
                      <td>
                        <span class={`settings-status-badge ${device.online ? "online" : "offline"}`}>
                          {device.online ? "Online" : "Offline"}
                        </span>
                      </td>
                      <td>
                        <Show
                          when={device.batteryLevel !== null && device.batteryLevel !== undefined}
                          fallback={<span class="settings-muted">-</span>}
                        >
                          <div class="settings-battery">
                            <span class={`settings-battery-level ${device.batteryLevel < 20 ? "low" : ""}`}>
                              {Math.round(device.batteryLevel)}%
                            </span>
                          </div>
                        </Show>
                      </td>
                      <td>{device.model || <span class="settings-muted">Unknown</span>}</td>
                      <td>{device.locationHint || <span class="settings-muted">-</span>}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </Show>
    </section>
  );
};